import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdExpandMore,
  MdExpandLess,
  MdLocalShipping,
  MdCheck,
  MdArrowForward,
  MdReceipt,
  MdShoppingCart,
} from 'react-icons/md';
import Breadcrumb from '../components/shared/Breadcrumb';
import Carousel from '../components/shared/Carousel';
import PopularCategories from '../components/shared/PopularCategories';
import image61 from '../assets/image61.png';
import image62 from '../assets/image62.png';
import image63 from '../assets/image63.png';
import image67 from '../assets/image67.png';
import { listMyOrders, getOrder, OrderStatus } from '../services/ordersApi';
import { useAuthStore } from '../store/authStore';
import '../styles/OrdersPage.css';

interface OrderItem {
  name: string;
  qty: number;
  price: number;
  image: string;
}

interface Order {
  id: number;
  orderNumber: string;
  items: OrderItem[];
  itemCount: number;
  detailLoaded: boolean;
  total: number;
  subtotal: number;
  shipping: number;
  tax: number;
  status: 'processing' | 'shipped' | 'delivered' | 'cancelled';
  date: string;
  paymentMethod: string;
  trackingNumber?: string;
  estimatedDelivery?: string;
}

const STATUS_CONFIG: Record<Order['status'], { label: string; color: string; bg: string }> = {
  processing: { label: 'Processing', color: '#3B6EF6', bg: 'rgba(59,110,246,0.08)' },
  shipped: { label: 'Shipped', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  delivered: { label: 'Delivered', color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
  cancelled: { label: 'Cancelled', color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
};

const PROMO_SLIDES = [
  { id: 1, image: image62, tag: 'WHOLESALE DEALS', title: 'Future-Proof Your Inventory', subtitle: 'Lock in wholesale pricing today.', cta: { label: 'Shop Now' }, bgColor: '#1A1040' },
  { id: 2, image: image61, tag: 'LIMITED TIME', title: 'Beverages at Bulk Prices', subtitle: 'Exclusive partner discounts this week.', cta: { label: 'View Deals' }, bgColor: '#2D1A0E' },
  { id: 3, image: image63, tag: 'ECO-FRIENDLY', title: 'Natural & Organic Range', subtitle: 'Delivered fast to your store.', cta: { label: 'Explore Now' }, bgColor: '#0A2E1A' },
];

type FilterTab = 'all' | 'processing' | 'shipped' | 'delivered' | 'cancelled';

/**
 * Map a backend OrderStatus → the page's UI status bucket. The backend has
 * more states than the UI cares about (pending/confirmed/invoiced collapse
 * to "processing"; shipped/delivered/cancelled pass through).
 */
function statusToUi(s: OrderStatus | string): Order['status'] {
  if (s === 'shipped') return 'shipped';
  if (s === 'delivered' || s === 'invoiced') return 'delivered';
  if (s === 'cancelled') return 'cancelled';
  return 'processing';
}

/**
 * Map a row from GET /customers/me/orders. That endpoint returns a SUMMARY
 * shape — `order_id`, `item_count`, `total_amount`, `status`, `created_at`
 * (no line items). Line items are fetched lazily via getOrder() on expand.
 */
function backendListOrderToUi(o: any): Order {
  const id = o.id ?? o.order_id;
  const tax = o.tax_total ?? 0;
  const total = o.total_amount ?? 0;
  return {
    id,
    orderNumber: `VIN-${id}`,
    items: [],
    itemCount: o.item_count ?? 0,
    detailLoaded: false,
    total,
    subtotal: Math.max(0, total - tax),
    shipping: 0,
    tax,
    status: statusToUi(o.status),
    date: new Date(o.created_at ?? o.order_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    paymentMethod: 'Direct Bank Transfer',
  };
}

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  // Orders come solely from /api/v1/customers/me/orders (the backend is the
  // single source of truth — no mock fallback).
  useEffect(() => {
    if (!isAuthenticated) {
      setOrders([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await listMyOrders({ limit: 50, sortBy: 'created_at', sortOrder: 'desc' });
        if (cancelled) return;
        setOrders((result.data ?? []).map(backendListOrderToUi));
      } catch (err) {
        if (!cancelled) {
          setOrders([]);
          setError(err instanceof Error ? err.message : 'Failed to load orders');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // The list endpoint returns no line items; fetch them lazily the first time
  // an order is expanded (GET /orders/:id) so the detail view is accurate.
  const handleToggleExpand = async (order: Order) => {
    const opening = expandedId !== order.orderNumber;
    setExpandedId(opening ? order.orderNumber : null);
    if (!opening || order.detailLoaded) return;
    try {
      const full = await getOrder(order.id);
      const items: OrderItem[] = (full.items ?? []).map((i) => ({
        name: i.product_name ?? i.sku_code ?? `SKU #${i.sku_id}`,
        qty: i.quantity,
        price: i.unit_price,
        image: image67,
      }));
      const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
      const tax = full.tax_total ?? order.tax;
      const total = full.total_amount ?? order.total;
      setOrders((prev) =>
        prev.map((o) =>
          o.orderNumber === order.orderNumber
            ? { ...o, items, itemCount: items.reduce((s, i) => s + i.qty, 0) || o.itemCount, subtotal, tax, shipping: Math.max(0, total - subtotal - tax), total, detailLoaded: true }
            : o,
        ),
      );
    } catch {
      // Leave the summary view; "View Full Details" still works.
    }
  };

  const filteredOrders = activeTab === 'all' ? orders : orders.filter((o) => o.status === activeTab);

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All Orders', count: orders.length },
    { key: 'processing', label: 'Processing', count: orders.filter((o) => o.status === 'processing').length },
    { key: 'shipped', label: 'Shipped', count: orders.filter((o) => o.status === 'shipped').length },
    { key: 'delivered', label: 'Delivered', count: orders.filter((o) => o.status === 'delivered').length },
  ];

  return (
    <div className="orders-page">
      <Breadcrumb />

      {/* ── Header ── */}
      <section className="orders-page__header-section">
        <div className="orders-page__header-inner">
          <div>
            <h1 className="orders-page__title">My Orders</h1>
            <p className="orders-page__subtitle">{orders.length} total orders</p>
          </div>
          <button className="orders-page__shop-btn" onClick={() => navigate('/products')}>
            Continue Shopping <MdArrowForward />
          </button>
        </div>
      </section>

      {/* ── Filter Tabs ── */}
      <section className="orders-page__tabs-section">
        <div className="orders-page__tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`orders-page__tab ${activeTab === tab.key ? 'orders-page__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className="orders-page__tab-count">{tab.count}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Orders List ── */}
      <section className="orders-page__list-section">
        {loading ? (
          <div className="orders-page__empty">
            <h2>Loading your orders…</h2>
          </div>
        ) : error ? (
          <div className="orders-page__empty">
            <MdShoppingCart className="orders-page__empty-icon" />
            <h2>Couldn't load orders</h2>
            <p>{error}</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="orders-page__empty">
            <MdShoppingCart className="orders-page__empty-icon" />
            <h2>No orders found</h2>
            <p>You don't have any {activeTab !== 'all' ? activeTab : ''} orders yet.</p>
          </div>
        ) : (
          <div className="orders-page__list">
            {filteredOrders.map((order, idx) => {
              const isExpanded = expandedId === order.orderNumber;
              const statusCfg = STATUS_CONFIG[order.status];
              return (
                <div
                  key={order.orderNumber}
                  className={`orders-page__order ${isExpanded ? 'orders-page__order--expanded' : ''}`}
                  style={{ animationDelay: `${idx * 0.06}s` }}
                >
                  {/* Order summary row */}
                  <div className="orders-page__order-header" onClick={() => handleToggleExpand(order)}>
                    <div className="orders-page__order-id">
                      <MdReceipt className="orders-page__order-id-icon" />
                      <div>
                        <span className="orders-page__order-number">#{order.orderNumber}</span>
                        <span className="orders-page__order-date">{order.date}</span>
                      </div>
                    </div>
                    <div className="orders-page__order-meta">
                      <span className="orders-page__order-items-count">
                        {order.itemCount} items
                      </span>
                      <span className="orders-page__order-total">${order.total.toFixed(2)}</span>
                      <span className="orders-page__status" style={{ color: statusCfg.color, background: statusCfg.bg }}>
                        {statusCfg.label}
                      </span>
                      <button className="orders-page__expand-btn" aria-label="Toggle details">
                        {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="orders-page__order-details">
                      {/* Items */}
                      <div className="orders-page__detail-section">
                        <h3 className="orders-page__detail-title">Order Items</h3>
                        <div className="orders-page__detail-items">
                          {order.items.map((item, i) => (
                            <div key={i} className="orders-page__detail-item">
                              <img src={item.image} alt={item.name} className="orders-page__detail-item-img" />
                              <div className="orders-page__detail-item-info">
                                <span className="orders-page__detail-item-name">{item.name}</span>
                                <span className="orders-page__detail-item-qty">Qty: {item.qty} × ${item.price.toFixed(2)}</span>
                              </div>
                              <span className="orders-page__detail-item-total">${(item.price * item.qty).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Summary + Timeline row */}
                      <div className="orders-page__detail-row">
                        {/* Cost breakdown */}
                        <div className="orders-page__detail-section orders-page__detail-section--summary">
                          <h3 className="orders-page__detail-title">Order Summary</h3>
                          <div className="orders-page__summary-rows">
                            <div className="orders-page__summary-row"><span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
                            <div className="orders-page__summary-row"><span>Shipping</span><span className={order.shipping === 0 ? 'orders-page__free' : ''}>{order.shipping === 0 ? 'Free' : `$${order.shipping.toFixed(2)}`}</span></div>
                            <div className="orders-page__summary-row"><span>Tax</span><span>${order.tax.toFixed(2)}</span></div>
                            <div className="orders-page__summary-row orders-page__summary-row--total"><span>Total</span><span>${order.total.toFixed(2)}</span></div>
                          </div>
                          <div className="orders-page__payment-info">
                            <span className="orders-page__payment-label">Payment</span>
                            <span>{order.paymentMethod}</span>
                          </div>
                        </div>

                        {/* Timeline */}
                        <div className="orders-page__detail-section orders-page__detail-section--timeline">
                          <h3 className="orders-page__detail-title">Tracking</h3>
                          {order.trackingNumber && (
                            <p className="orders-page__tracking-num">
                              <MdLocalShipping /> {order.trackingNumber}
                            </p>
                          )}
                          <div className="orders-page__mini-timeline">
                            <div className={`orders-page__tl-step ${['processing', 'shipped', 'delivered'].includes(order.status) ? 'orders-page__tl-step--done' : ''}`}>
                              <div className="orders-page__tl-dot"><MdCheck /></div>
                              <span>Ordered</span>
                            </div>
                            <div className={`orders-page__tl-step ${['shipped', 'delivered'].includes(order.status) ? 'orders-page__tl-step--done' : order.status === 'processing' ? 'orders-page__tl-step--active' : ''}`}>
                              <div className="orders-page__tl-dot">{['shipped', 'delivered'].includes(order.status) ? <MdCheck /> : null}</div>
                              <span>Processing</span>
                            </div>
                            <div className={`orders-page__tl-step ${order.status === 'delivered' ? 'orders-page__tl-step--done' : order.status === 'shipped' ? 'orders-page__tl-step--active' : ''}`}>
                              <div className="orders-page__tl-dot">{order.status === 'delivered' ? <MdCheck /> : null}</div>
                              <span>Shipped</span>
                            </div>
                            <div className={`orders-page__tl-step ${order.status === 'delivered' ? 'orders-page__tl-step--done' : ''}`}>
                              <div className="orders-page__tl-dot">{order.status === 'delivered' ? <MdCheck /> : null}</div>
                              <span>Delivered</span>
                            </div>
                          </div>
                          {order.estimatedDelivery && (
                            <p className="orders-page__est-delivery">Est. delivery: <strong>{order.estimatedDelivery}</strong></p>
                          )}
                        </div>
                      </div>

                      <div className="orders-page__detail-actions">
                        <button className="orders-page__view-detail-btn" onClick={() => navigate(`/orders/${order.orderNumber}`)}>
                          View Full Details <MdArrowForward />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Carousel ── */}
      <section className="orders-page__carousel">
        <Carousel slides={PROMO_SLIDES} autoPlay interval={5000} />
      </section>

      {/* ── Popular Categories ── */}
      <PopularCategories />
    </div>
  );
};

export default OrdersPage;
